import type { QueryKey } from '@tanstack/react-query';
import type { EntityChangedEvent } from '@/services/realtime.service';

/**
 * Which React Query caches a server-side change makes stale.
 *
 * Keys are the API's audit entity types (singular nouns — the same strings
 * the Activity Log shows). Values are query-key ROOTS: `invalidateQueries`
 * matches by prefix, so `['patients']` covers the list, every filtered list
 * and every `['patients', 'detail', id]` underneath it.
 *
 * Over-invalidating is cheap (a refetch of what is on screen, nothing for
 * what is not) and under-invalidating is a stale screen, so when in doubt an
 * entity lists more roots, not fewer. Dashboards and reports that aggregate
 * a family of records are grouped below so each entity names the family.
 */

/** Everything that adds up appointments. */
const SCHEDULE_AGGREGATES: QueryKey[] = [
  ['home'],
  ['dashboard-stats'],
  ['receptionist-dashboard'],
  ['recent-appointments'],
  ['appointments-trend'],
  ['advanced-operations'],
  ['advanced-kpis'],
  ['advanced-clinic-kpis'],
];

/** Everything that adds up money. */
const FINANCE_AGGREGATES: QueryKey[] = [
  ['home'],
  ['dashboard-stats'],
  ['finance-overview'],
  ['financial-summary'],
  ['monthly-financial-summary'],
  ['transactions'],
  ['recent-transactions'],
  ['cash-flow'],
  ['balance-sheet'],
  // Each owner's equity carries their share of profit.
  ['clinic-owners'],
  ['my-ownership'],
  ['cash-reconciliation'],
  ['pending-invoices'],
  ['report-receivables'],
  ['report-patient-statement'],
  ['revenue-by-doctor'],
  ['monthly-revenue-trend'],
  ['advanced-revenue-trend'],
  ['advanced-kpis'],
  ['advanced-clinic-kpis'],
];

const PATIENT_MONEY: QueryKey[] = [
  ['patients'],
  ['patient-balance'],
  ['reservation-detail'],
];

const STOCK: QueryKey[] = [
  ['stock-levels'],
  ['stock-purchases'],
  ['inventory-value'],
  ['material-costs'],
];

export const ENTITY_QUERY_ROOTS: Record<string, QueryKey[]> = {
  // ── Patients & clinical ─────────────────────────────────────────────────
  patient: [['patients'], ['patient-balance'], ['home'], ['dashboard-stats']],
  'medical-history': [['patients'], ['reservation-detail']],
  medical_history: [['patients'], ['reservation-detail']],
  'medical-record': [['patients'], ['reservation-detail']],
  allergy: [['patients'], ['reservation-detail']],
  diagnosis: [['patients'], ['reservation-detail']],
  'patient-chart': [['patients'], ['reservation-detail']],
  chart: [['patients'], ['reservation-detail']],
  photo: [['patients'], ['reservation-detail'], ['photo']],
  prescription: [['patients'], ['reservation-detail']],
  'treatment-plan': [['treatment-plan'], ['patients'], ['reservation-detail']],
  treatment_plan_item: [
    ['treatment-plan'],
    ['patients'],
    ['reservation-detail'],
  ],
  'treatment-item': [['reservation-detail'], ['treatment-plan'], ['patients']],
  recall: [['patients'], ['home']],
  'patient-coverage': [['patients'], ['reservation-detail']],
  patient_coverage: [['patients'], ['reservation-detail']],
  'patient-wallet': [...PATIENT_MONEY, ...FINANCE_AGGREGATES],

  // ── Schedule ─────────────────────────────────────────────────────────────
  reservation: [
    ['reservations'],
    ['reservation-detail'],
    ['queue'],
    ['patients'],
    ...SCHEDULE_AGGREGATES,
  ],
  'reservation-fee': [
    ['reservations'],
    ['reservation-detail'],
    ...PATIENT_MONEY,
  ],
  'reservation-invoice': [
    ['reservations'],
    ['reservation-detail'],
    ...PATIENT_MONEY,
    ...FINANCE_AGGREGATES,
  ],
  'reservation-payment': [
    ['reservations'],
    ['reservation-detail'],
    ...PATIENT_MONEY,
    ...FINANCE_AGGREGATES,
  ],
  queue: [['queue'], ['reservations'], ...SCHEDULE_AGGREGATES],
  waitlist: [['reservations'], ['home']],
  cancellation: [
    ['reservations'],
    ['reservation-detail'],
    ...SCHEDULE_AGGREGATES,
  ],
  'schedule-exception': [['reservations'], ['branch-working-hours']],
  'working-hours': [['branch-working-hours'], ['reservations']],
  room: [['reservations'], ['queue'], ['branch-options']],

  // ── Money ────────────────────────────────────────────────────────────────
  invoice: [...PATIENT_MONEY, ...FINANCE_AGGREGATES, ['reservations']],
  payment: [...PATIENT_MONEY, ...FINANCE_AGGREGATES, ['reservations']],
  transaction: [
    ...FINANCE_AGGREGATES,
    ['financial-accounts'],
    ['finance-accounts'],
  ],
  expense: [...FINANCE_AGGREGATES],
  'recurring-expense': [...FINANCE_AGGREGATES],
  'financial-account': [
    ['financial-accounts'],
    ['finance-accounts'],
    ...FINANCE_AGGREGATES,
  ],
  'fixed-asset': [['fixed-assets'], ['balance-sheet']],
  'owner-equity': [['owner-equity'], ['balance-sheet'], ...FINANCE_AGGREGATES],
  'clinic-owner': [['clinic-owners'], ['my-ownership']],
  loyalty: [['patients'], ['patient-balance']],
  offer: [['reservation-detail'], ['patients']],

  // ── Insurance ────────────────────────────────────────────────────────────
  insurance_claim: [['insurance'], ...PATIENT_MONEY, ...FINANCE_AGGREGATES],
  insurance_batch: [['insurance'], ...FINANCE_AGGREGATES],
  insurance_company: [['insurance'], ['patients']],
  insurance_plan: [['insurance'], ['patients']],

  // ── Lab ──────────────────────────────────────────────────────────────────
  lab_request: [
    ['lab-requests'],
    ['lab'],
    ['labs'],
    ['patients'],
    ['reservation-detail'],
  ],

  // ── Inventory ────────────────────────────────────────────────────────────
  material: [...STOCK, ['material-usage'], ['material-consumption']],
  'material-variant': [...STOCK],
  'material-stock': [...STOCK],
  'material-consumption': [
    ...STOCK,
    ['material-consumption'],
    ['material-consumption-summary'],
    ['material-usage'],
    ['material-usage-summary'],
    ['material-usage-details'],
    ['material-usage-by-patient'],
    ['material-usage-by-material'],
    ['material-usage-by-doctor'],
  ],
  'stock-purchase': [...STOCK, ['suppliers'], ...FINANCE_AGGREGATES],
  'stock-count': [
    ...STOCK,
    ['stock-count-sessions'],
    ['stock-count-materials'],
  ],
  supplier: [['suppliers'], ['stock-purchases']],

  // ── People & configuration ──────────────────────────────────────────────
  staff: [['staff'], ['doctor-options'], ['reservations'], ['queue']],
  staff_compensation: [['staff']],
  payroll_period: [['payroll'], ...FINANCE_AGGREGATES],
  payout: [['payroll'], ...FINANCE_AGGREGATES],
  role: [['staff'], ['roles']],
  permissions: [['staff'], ['roles']],
  invitation: [['staff'], ['invitations']],
  branch: [
    ['branch-options'],
    ['branch-usage'],
    ['branch-working-hours'],
    ['clinics'],
  ],
  clinic: [['clinics'], ['booking-access'], ['finance-overview']],
  procedure: [['procedures'], ['treatment-plan'], ['reservation-detail']],
  'procedure-price': [
    ['procedures'],
    ['treatment-plan'],
    ['reservation-detail'],
  ],
  medicine: [['medicines'], ['patients']],
  device: [['devices']],
  subscription: [
    ['subscription'],
    ['subscription-status'],
    ['subscription-limits'],
    ['billing'],
  ],
  'feature-request': [['feature-requests']],
};

/**
 * Roots for an entity nobody has mapped yet: the entity name itself in the
 * spellings this codebase uses for keys (`invoice` → `invoices`,
 * `lab_request` → `lab-requests`). A new module gets a sensible default
 * without editing this file; a better mapping is one line above.
 */
function guessRoots(entity: string): QueryKey[] {
  const kebab = entity.replace(/_/g, '-');
  const roots = new Set<string>([entity, kebab, `${entity}s`, `${kebab}s`]);
  return Array.from(roots).map((r) => [r]);
}

/** Every query root made stale by one change. */
export function rootsFor(event: EntityChangedEvent): QueryKey[] {
  const roots = ENTITY_QUERY_ROOTS[event.entity] ?? guessRoots(event.entity);
  // A change filed under a patient (a payment, a chart entry) also dates
  // that patient's profile even when the entity's own roots do not say so.
  if (event.subjectType === 'patient' && event.subjectId) {
    return [...roots, ['patients', 'detail', event.subjectId]];
  }
  return roots;
}
